import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Store, Check } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { VENUE_TYPES, UAE_CITIES } from '../lib/venueTypes';

const CATEGORIES = ['cafe', 'restaurant', 'bar', 'fitness', 'wellness', 'experience', 'retail', 'other'];

export default function BusinessRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const inviteToken = params.get('invite') || '';

  const [form, setForm] = useState({
    name: '', category: 'cafe', business_types: [], city: 'Abu Dhabi',
    description: '', address: '',
    contact_email: '', contact_phone: '',
    website: '', instagram: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    // If logged-in user has a profile email, prefill
    if (user?.email && !form.contact_email) {
      setForm(f => ({ ...f, contact_email: user.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user) {
      navigate('/login');
      return;
    }
    if (!form.name.trim()) return setError('Business name is required');
    if (form.business_types.length === 0) return setError('Pick at least one venue type');
    setSubmitting(true);
    try {
      const { data } = await api.post('/businesses/register', {
        ...form,
        invite_token: inviteToken,
      });
      setSuccess(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="content-area pb-12" data-testid="business-register-success">
        <div className="px-4 pt-6 max-w-md mx-auto">
          <div className="bg-[#B6E600]/15 border border-[#B6E600]/30 rounded-3xl p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#B6E600] flex items-center justify-center mx-auto mb-3">
              <Check size={28} className="text-[#111]" strokeWidth={3} />
            </div>
            <h1 className="text-xl font-extrabold text-[#111111]">You're on ASAP</h1>
            <p className="text-sm text-[#6B6E73] mt-2">
              {success.name} is now live. Create your first group offer to start
              attracting bookings.
            </p>
            <button
              data-testid="business-register-go-profile"
              onClick={() => navigate(`/business/${success.id}`)}
              className="mt-5 w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-3 text-sm"
            >
              Go to my business page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-area pb-12" data-testid="business-register-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="biz-register-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} className="text-[#111]" />
        </button>
        <div className="flex items-center gap-2">
          <Store size={20} className="text-[#B6E600]" />
          <h1 className="text-xl font-bold tracking-tight text-[#111111]">Register your venue</h1>
        </div>
      </div>

      <div className="px-4 max-w-md mx-auto">
        {inviteToken && (
          <div data-testid="biz-register-invite-banner" className="mb-4 px-4 py-3 rounded-2xl bg-[#B6E600]/15 border border-[#B6E600]/35 text-xs text-[#3F5200]">
            🎉 You were invited by an ASAP host. The host earns community points when you complete registration.
          </div>
        )}
        <p className="text-sm text-[#6B6E73] mb-5">
          List your venue on ASAP so hosts can choose you as their partner venue.
          Free during beta — create exclusive group offers and reach social groups
          already booking in your city.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Business name" required>
            <input
              data-testid="biz-name"
              type="text"
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="e.g. Brew Lab"
              className="biz-input"
            />
          </Field>

          <Field label="Category">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => (
                <button
                  key={c}
                  type="button"
                  data-testid={`biz-cat-${c}`}
                  onClick={() => update('category', c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.category === c ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Venue type" required>
            <p className="text-[11px] text-[#9B9EA3] -mt-0.5 mb-2 leading-relaxed">
              Pick up to 3. A hotel might be Hotel/Lounge + Restaurant; a cafe could
              also fit Coworking Space. Multi-tagging makes you discoverable for more
              plan categories.
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="biz-venue-types">
              {VENUE_TYPES.map(t => {
                const picked = form.business_types.includes(t);
                const limitReached = !picked && form.business_types.length >= 3;
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`biz-vtype-${t.replace(/[^a-zA-Z]/g, '-')}`}
                    aria-pressed={picked}
                    disabled={limitReached}
                    onClick={() => {
                      setForm(f => ({
                        ...f,
                        business_types: picked
                          ? f.business_types.filter(x => x !== t)
                          : [...f.business_types, t],
                      }));
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      picked
                        ? 'bg-[#B6E600] text-[#111] border border-[#B6E600]'
                        : limitReached
                          ? 'bg-[#F1F0EA] text-[#B0B3B8] border border-black/[0.04] opacity-50'
                          : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'
                    }`}
                  >
                    {picked && '✓ '}{t}
                  </button>
                );
              })}
            </div>
            <p data-testid="biz-vtype-count" className="text-[10px] text-[#9B9EA3] mt-2">
              {form.business_types.length}/3 selected
            </p>
          </Field>

          <Field label="City (UAE only for v1)" required>
            <div className="flex flex-wrap gap-1.5">
              {UAE_CITIES.map(c => (
                <button
                  key={c}
                  type="button"
                  data-testid={`biz-city-${c.replace(/\s+/g, '-')}`}
                  onClick={() => update('city', c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.city === c ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Description">
            <textarea
              data-testid="biz-description"
              value={form.description}
              onChange={e => update('description', e.target.value)}
              rows={3}
              placeholder="What makes your venue great for groups?"
              className="biz-input resize-none"
            />
          </Field>

          <Field label="Address">
            <input
              data-testid="biz-address"
              type="text"
              value={form.address}
              onChange={e => update('address', e.target.value)}
              placeholder="Building, area"
              className="biz-input"
            />
          </Field>

          <Field label="Contact email" required>
            <input
              data-testid="biz-email"
              type="email"
              value={form.contact_email}
              onChange={e => update('contact_email', e.target.value)}
              placeholder="hello@yourvenue.ae"
              className="biz-input"
            />
          </Field>

          <Field label="Contact phone">
            <input
              data-testid="biz-phone"
              type="tel"
              value={form.contact_phone}
              onChange={e => update('contact_phone', e.target.value)}
              placeholder="+971 …"
              className="biz-input"
            />
          </Field>

          <Field label="Website">
            <input
              data-testid="biz-website"
              type="url"
              value={form.website}
              onChange={e => update('website', e.target.value)}
              placeholder="https://"
              className="biz-input"
            />
          </Field>

          <Field label="Instagram">
            <input
              data-testid="biz-instagram"
              type="text"
              value={form.instagram}
              onChange={e => update('instagram', e.target.value)}
              placeholder="@handle"
              className="biz-input"
            />
          </Field>

          {error && (
            <div data-testid="biz-register-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-3 py-2.5 text-[#FF3B30] text-sm">
              {error}
            </div>
          )}

          <button
            data-testid="biz-register-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-3.5 text-sm active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {submitting ? 'Registering…' : 'Register my venue'}
          </button>
        </form>
      </div>

      <style>{`
        .biz-input {
          width: 100%; background:#fff; color:#111; border-radius:14px;
          padding:10px 12px; font-size:14px; border:1px solid rgba(0,0,0,0.06);
        }
        .biz-input:focus { outline:none; border-color: rgba(182,230,0,0.45); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-[#FF3B30]">*</span>}
      </label>
      {children}
    </div>
  );
}
